import {
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  type ListFilter,
  type PrimaryFilterId,
  type StaticListFilter,
  type TreeFilter,
} from '../../../filters/types/filters';
import type { ReportParameterConfig } from '../../PageConfigProvider/page-reducer';
import type { ParameterSelection } from './check-datapoint-in-parameter';

const extractLeafId = (id: string): string => id.split('_')?.pop() ?? id;

function normalizeSelectionId(filterId: FilterId, selectionId: string): string {
  if (filterId === FilterId.COMPANY) {
    return extractLeafId(selectionId);
  }
  return selectionId;
}

type ParameterSelectionsFilter =
  | Pick<
      TreeFilter,
      'id' | 'type' | 'selections' | 'primarySelections' | 'isPrimary'
    >
  | Pick<ListFilter, 'id' | 'type' | 'selections'>
  | Pick<StaticListFilter, 'id' | 'type' | 'selections'>;

function mapFilterToParameterSelections(
  filter: ParameterSelectionsFilter
): ParameterSelection[] {
  const mapSelections = (
    selections: ReadonlyArray<ParameterSelection>
  ): ParameterSelection[] =>
    selections.map((selection) => ({
      ...selection,
      id: normalizeSelectionId(filter.id, selection.id),
    }));

  switch (filter.type) {
    case FilterType.TREE: {
      const source = (() => {
        if (filter.isPrimary !== true) {
          return filter.selections;
        }
        const activePrimary = filter.primarySelections ?? [];
        if (activePrimary.length > 0) {
          return activePrimary;
        }
        return filter.selections;
      })();
      return mapSelections(source);
    }
    case FilterType.LIST:
    case FilterType.STATIC_LIST:
      return mapSelections(filter.selections);
    default:
      return [];
  }
}

export const getPrimaryParameterSelections = ({
  reportParameters,
  activeFilterMap,
  primaryFilterId,
}: {
  reportParameters: ReportParameterConfig[];
  activeFilterMap: FilterSelectionsMap;
  primaryFilterId?: PrimaryFilterId;
}) => {
  const primaryParameter = reportParameters.find((p) => p.isPrimary);

  const resolveFromFilterId = (filterId: FilterId): ParameterSelection[] => {
    const filter = activeFilterMap.get(filterId);
    if (
      !filter ||
      (filter.type !== FilterType.TREE &&
        filter.type !== FilterType.STATIC_LIST &&
        filter.type !== FilterType.LIST)
    ) {
      return [];
    }

    return mapFilterToParameterSelections(filter);
  };

  if (primaryParameter) {
    return resolveFromFilterId(primaryParameter.filterId);
  }

  if (primaryFilterId) {
    return resolveFromFilterId(primaryFilterId);
  }

  return [];
};
